// ─── Demo Scenario Definitions ───
// Three complete demo scenarios with full task trees, personas, and story points.
// Avatar URLs use the storage bucket path and will be populated after generate-demo-avatars runs.

const STORAGE_BASE = `https://crerrzxnzwoghskavcvf.supabase.co/storage/v1/object/public/avatars/demo`;

import { CAKE_STORY, COURSE_STORY, VIDEO_STORY } from './demoStories';

// ─── Types ───

export interface DemoPersona {
  name: string;
  role: 'owner' | 'admin' | 'manager' | 'user' | 'visitor';
  avatarUrl: string;
  slotKey: string;
}

export interface DemoTask {
  name: string;
  assigneeSlot: string;
  durationHours: number;
  parentName?: string;
  folder?: string;
  checklist?: string[];
  mediaUrl?: string;
  mediaTotalPages?: number;
  intent?: string;
  specs?: string[];
  scheduleMode?: 'hourly' | 'daily';
}

export interface DemoDependency {
  from: string;
  to: string;
  type: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
}

export interface DemoApproval {
  taskName: string;
  approverSlots: string[];
}

export interface DemoAutoAction {
  type: 'check-item' | 'type-comment' | 'mark-progress' | 'approve-task' | 'reject-task' | 'resubmit-approval' |
        'open-panel' | 'close-panel' | 'close-media' | 'scroll-gantt' | 'open-media' | 'add-media-version' | 'add-review-note' |
        'trigger-notification' | 'show-dashboard' | 'trigger-checkin' | 'wait' | 'complete-all-tasks' | 'add-approver';
  target?: string;
  value?: string;
  delay?: number;
  actAsSlot?: string;
  /** Seconds into the media for timecoded review notes. */
  timestamp?: number;
}

export interface DemoStoryPoint {
  id: string;
  actAsSlot: string;
  taskTarget?: string;
  highlight?: string;
  narrative: string;
  featureCallout?: string;
  autoActions?: DemoAutoAction[];
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'bottom-screen';
  isFinalStep?: boolean;
  /** Short chapter title shown in the chapter menu. */
  title?: string;
  /** Scripted sample setup rather than a live decision; shown on screen. */
  fixture?: boolean;
  /**
   * Interactive review checkpoint: the presenter makes the real decision in
   * the app, signed in as themselves. The chapter's approve/reject action for
   * this reviewer is what they are expected to do; it is not written for them.
   */
  checkpoint?: { target: string; approverSlot: string };
  /** Optional hands-on exploration hint (feature not driven automatically). */
  explore?: string;
}

export interface DemoScenario {
  id: string;
  name: string;
  description: string;
  category: string;
  scheduleMode?: 'hourly' | 'daily';
  personas: Record<string, DemoPersona>;
  tasks: DemoTask[];
  dependencies: DemoDependency[];
  approvals: DemoApproval[];
  storyPoints: DemoStoryPoint[];
  /** Presenter guidance, in minutes, for the full chapter list. */
  runtimeMinutes?: number;
}


// ═══════════════════════════════════════════
// DEMO 1: Bake a Cake (15 chapters)
// ═══════════════════════════════════════════


const cakePersonas: Record<string, DemoPersona> = {
  owner: { name: 'Owner Tony', role: 'owner', avatarUrl: `${STORAGE_BASE}/cake-tony.png`, slotKey: 'owner' },
  admin: { name: 'Executive Chef Linda', role: 'admin', avatarUrl: `${STORAGE_BASE}/cake-linda.png`, slotKey: 'admin' },
  manager: { name: 'Sous Chef Omar', role: 'manager', avatarUrl: `${STORAGE_BASE}/cake-omar.png`, slotKey: 'manager' },
  user_1: { name: 'Junior Cook Marcy', role: 'user', avatarUrl: `${STORAGE_BASE}/cake-marcy.png`, slotKey: 'user_1' },
  user_2: { name: 'Junior Cook Juan', role: 'user', avatarUrl: `${STORAGE_BASE}/cake-juan.png`, slotKey: 'user_2' },
};

const cakeTasks: DemoTask[] = [
  { name: 'Bake a Cake', assigneeSlot: 'admin', durationHours: 5, intent: 'Create a three-layer birthday cake for an upcoming celebration.', specs: ['Three layers', 'Vanilla frosting', 'Birthday decorations'] },
  { name: 'Gather ingredients', assigneeSlot: 'manager', durationHours: 1, parentName: 'Bake a Cake', checklist: ['Flour', 'Sugar', 'Eggs', 'Butter', 'Milk', 'Baking powder'], intent: 'Source and prepare all required baking ingredients.' },
  { name: 'Mix ingredients', assigneeSlot: 'manager', durationHours: 2, parentName: 'Bake a Cake', intent: 'Combine wet and dry ingredients into batter.' },
  { name: 'Dry ingredients', assigneeSlot: 'user_1', durationHours: 1, parentName: 'Mix ingredients', checklist: ['Sift flour', 'Measure sugar', 'Add baking powder'], intent: 'Prepare the dry ingredient mixture.' },
  { name: 'Wet ingredients', assigneeSlot: 'user_2', durationHours: 1, parentName: 'Mix ingredients', checklist: ['Crack eggs', 'Melt butter', 'Measure milk'], intent: 'Prepare the wet ingredient mixture.' },
  { name: 'Bake the cake', assigneeSlot: 'manager', durationHours: 2, parentName: 'Bake a Cake', checklist: ['Preheat oven to 350°F', 'Combine wet and dry mixtures', 'Pour into pans', 'Bake for 30 minutes', 'Cool on wire rack'], intent: 'Bake the combined batter into cake layers.' },
];

const cakeDeps: DemoDependency[] = [
  { from: 'Gather ingredients', to: 'Mix ingredients', type: 'finish-to-start' },
  { from: 'Mix ingredients', to: 'Bake the cake', type: 'finish-to-start' },
  { from: 'Dry ingredients', to: 'Wet ingredients', type: 'start-to-start' },
];

const cakeApprovals: DemoApproval[] = [
  { taskName: 'Wet ingredients', approverSlots: ['manager'] },
  { taskName: 'Bake the cake', approverSlots: ['admin'] },
];

const cakeStoryPoints: DemoStoryPoint[] = CAKE_STORY;

const cakeScenario: DemoScenario = {
  id: 'demo-cake',
  name: 'Bake a Cake',
  description: 'A kitchen team bakes a birthday cake: task sequence, parallel preparation, action items, roles and a review with a revision round.',
  category: 'food',
  runtimeMinutes: 8,
  personas: cakePersonas,
  tasks: cakeTasks,
  dependencies: cakeDeps,
  approvals: cakeApprovals,
  storyPoints: cakeStoryPoints,
};

// ═══════════════════════════════════════════
// DEMO 2: Course Design (22 chapters)
// ═══════════════════════════════════════════


const coursePersonas: Record<string, DemoPersona> = {
  owner: { name: 'Executive Director Silas', role: 'owner', avatarUrl: `${STORAGE_BASE}/course-silas.png`, slotKey: 'owner' },
  admin: { name: 'Director Terry', role: 'admin', avatarUrl: `${STORAGE_BASE}/course-terry.png`, slotKey: 'admin' },
  manager: { name: 'Asst Director Phyllis', role: 'manager', avatarUrl: `${STORAGE_BASE}/course-phyllis.png`, slotKey: 'manager' },
  user_1: { name: 'Designer Salma', role: 'user', avatarUrl: `${STORAGE_BASE}/course-salma.png`, slotKey: 'user_1' },
  user_2: { name: 'Designer Toby', role: 'user', avatarUrl: `${STORAGE_BASE}/course-toby.png`, slotKey: 'user_2' },
  visitor: { name: 'Instructor Tom', role: 'visitor', avatarUrl: `${STORAGE_BASE}/course-tom.png`, slotKey: 'visitor' },
};

const courseTasks: DemoTask[] = [
  { name: 'Life Skills 101', assigneeSlot: 'admin', durationHours: 160, intent: 'Design and launch a comprehensive life skills course for adult learners.', specs: ['12 modules', 'Video + text content', 'Quizzes and assignments'] },
  { name: 'Planning', assigneeSlot: 'manager', durationHours: 48, parentName: 'Life Skills 101', intent: 'Plan the course structure, content strategy, and media approach.' },
  { name: 'SME Onboarding', assigneeSlot: 'manager', durationHours: 16, parentName: 'Planning', checklist: ['Schedule kickoff meeting', 'Share course goals', 'Collect SME availability', 'Define review cadence'], intent: 'Onboard the subject matter expert.' },
  { name: 'Course Outline', assigneeSlot: 'user_1', durationHours: 16, parentName: 'Planning', checklist: ['Draft module structure', 'Map learning objectives', 'Define assessments', 'Review with SME'], intent: 'Create the course content outline.', mediaUrl: '/demo-media/course-outline-life-skills-101.pdf', mediaTotalPages: 3 },
  { name: 'Media Planning', assigneeSlot: 'user_2', durationHours: 16, parentName: 'Planning', checklist: ['Inventory existing assets', 'Script video concepts', 'Plan graphics', 'Schedule recordings'], intent: 'Plan all media production needs.' },
  { name: 'Course Materials', assigneeSlot: 'manager', durationHours: 96, parentName: 'Life Skills 101', intent: 'Produce all course learning materials.' },
  { name: 'Assignments', assigneeSlot: 'user_1', durationHours: 32, parentName: 'Course Materials', checklist: ['SME submits content', 'Designer reviews', 'Revise based on feedback', 'Post to LMS'], intent: 'Create course assignments.' },
  { name: 'Quizzes', assigneeSlot: 'user_1', durationHours: 32, parentName: 'Course Materials', checklist: ['SME submits questions', 'Designer formats', 'Validate answer keys', 'Post to LMS'], intent: 'Create assessment quizzes.' },
  { name: 'Course Media', assigneeSlot: 'user_2', durationHours: 32, parentName: 'Course Materials', checklist: ['Write scripts', 'Record videos', 'Edit footage', 'Approve with SME'], intent: 'Produce all video and media content.', mediaUrl: '/demo-media/review-cut-v1.mp4' },
  { name: 'Course Launch', assigneeSlot: 'manager', durationHours: 8, parentName: 'Life Skills 101', checklist: ['Verify all LMS content', 'Test student access', 'Final director approval', 'Go live announcement'], intent: 'Launch the completed course.' },
];

const courseDeps: DemoDependency[] = [
  { from: 'Planning', to: 'Course Materials', type: 'finish-to-start' },
  { from: 'Assignments', to: 'Quizzes', type: 'start-to-start' },
  { from: 'Course Materials', to: 'Course Launch', type: 'finish-to-start' },
];

const courseApprovals: DemoApproval[] = [
  { taskName: 'Course Outline', approverSlots: ['visitor'] },
  { taskName: 'Assignments', approverSlots: ['manager'] },
  { taskName: 'Course Media', approverSlots: ['visitor'] },
  { taskName: 'Course Launch', approverSlots: ['admin'] },
];

const courseStoryPoints: DemoStoryPoint[] = COURSE_STORY;

const courseScenario: DemoScenario = {
  id: 'demo-course',
  name: 'Course Design',
  description: 'An education team builds "Life Skills 101": a PDF page note, a timecoded video review with versions, parallel materials, a TaskBot check-in and a live launch approval.',
  runtimeMinutes: 12,
  category: 'education',
  scheduleMode: 'daily',
  personas: coursePersonas,
  tasks: courseTasks,
  dependencies: courseDeps,
  approvals: courseApprovals,
  storyPoints: courseStoryPoints,
};

// ═══════════════════════════════════════════
// DEMO 3: Marketing Video (21 chapters)
// ═══════════════════════════════════════════


const videoPersonas: Record<string, DemoPersona> = {
  owner: { name: 'Executive Producer Wendy', role: 'owner', avatarUrl: `${STORAGE_BASE}/video-wendy.png`, slotKey: 'owner' },
  admin: { name: 'Director Alejandro', role: 'admin', avatarUrl: `${STORAGE_BASE}/video-alejandro.jpg`, slotKey: 'admin' },
  manager: { name: 'Producer Bob', role: 'manager', avatarUrl: `${STORAGE_BASE}/video-bob.png`, slotKey: 'manager' },
  user_1: { name: 'Cinematographer Jake', role: 'user', avatarUrl: `${STORAGE_BASE}/video-jake.png`, slotKey: 'user_1' },
  user_2: { name: 'Editor Vajra', role: 'user', avatarUrl: `${STORAGE_BASE}/video-vajra.png`, slotKey: 'user_2' },
  visitor: { name: 'Client Natalie', role: 'visitor', avatarUrl: `${STORAGE_BASE}/video-natalie.png`, slotKey: 'visitor' },
};

const videoTasks: DemoTask[] = [
  { name: 'Marketing Video', assigneeSlot: 'owner', durationHours: 176, intent: 'Produce a 60-second marketing video for the brand relaunch campaign.', specs: ['60 seconds', '4K resolution', 'Brand guidelines compliant', 'Social media + web versions'] },
  { name: 'Pre-Production', assigneeSlot: 'admin', durationHours: 48, parentName: 'Marketing Video', intent: 'Plan and approve all creative elements before production begins.' },
  { name: 'Concept', assigneeSlot: 'manager', durationHours: 16, parentName: 'Pre-Production', checklist: ['Research target audience', 'Brainstorm concepts', 'Create mood board', 'Present 3 options to director'], intent: 'Develop the creative concept.' },
  { name: 'Script', assigneeSlot: 'manager', durationHours: 16, parentName: 'Pre-Production', checklist: ['Write first draft', 'Internal review', 'Director feedback', 'Final script lock'], intent: 'Write the video script.', mediaUrl: '/demo-media/marketing-video-script-sample.pdf', mediaTotalPages: 3 },
  { name: 'Storyboard', assigneeSlot: 'user_2', durationHours: 16, parentName: 'Pre-Production', checklist: ['Sketch key frames', 'Add camera notes', 'Include timing marks', 'Client review meeting'], intent: 'Create the visual storyboard.', mediaUrl: '/demo-media/marketing-video-storyboard-sample.pdf', mediaTotalPages: 3 },
  { name: 'Production', assigneeSlot: 'manager', durationHours: 32, parentName: 'Marketing Video', intent: 'Execute the shoot and capture all footage.' },
  { name: 'Schedule Shoot', assigneeSlot: 'manager', durationHours: 8, parentName: 'Production', checklist: ['Book studio/location', 'Hire talent', 'Arrange equipment', 'Send call sheets'], intent: 'Coordinate all shoot logistics.' },
  { name: 'Record', assigneeSlot: 'user_1', durationHours: 16, parentName: 'Production', checklist: ['Camera setup', 'Lighting check', 'Record takes', 'Review dailies', 'Backup footage'], intent: 'Capture all video footage.' },
  { name: 'Ingest footage', assigneeSlot: 'user_1', durationHours: 8, parentName: 'Production', checklist: ['Transfer media', 'Organize by scene', 'Create proxies', 'Sync audio'], intent: 'Import and organize all captured footage.' },
  { name: 'Post Production', assigneeSlot: 'manager', durationHours: 80, parentName: 'Marketing Video', intent: 'Edit, refine, and finalize the video.' },
  { name: 'Assembly Cut', assigneeSlot: 'user_2', durationHours: 24, parentName: 'Post Production', checklist: ['Import timeline', 'Rough sequence', 'Add placeholder audio', 'Producer review'], intent: 'Create the first rough assembly.', mediaUrl: '/demo-media/review-cut-v1.mp4' },
  { name: 'Rough Cut', assigneeSlot: 'user_2', durationHours: 32, parentName: 'Post Production', checklist: ['Refine pacing', 'Add music bed', 'Color correction pass', 'Sound design', 'Client review screening'], intent: 'Refine the edit for client review.', mediaUrl: '/demo-media/review-cut-v1.mp4' },
  { name: 'Final Cut', assigneeSlot: 'user_2', durationHours: 24, parentName: 'Post Production', checklist: ['Incorporate client notes', 'Final color grade', 'Audio mix and master', 'Add titles and graphics', 'Quality check all formats'], intent: 'Complete the final deliverable.', mediaUrl: '/demo-media/review-cut-v2.mp4' },
  { name: 'Delivery', assigneeSlot: 'admin', durationHours: 16, parentName: 'Marketing Video', checklist: ['Render all formats', 'Upload to client portal', 'Send delivery notification', 'Archive project files'], intent: 'Deliver final assets to the client.' },
];

const videoDeps: DemoDependency[] = [
  { from: 'Concept', to: 'Script', type: 'finish-to-start' },
  { from: 'Script', to: 'Storyboard', type: 'finish-to-start' },
  { from: 'Pre-Production', to: 'Production', type: 'finish-to-start' },
  { from: 'Schedule Shoot', to: 'Record', type: 'finish-to-start' },
  { from: 'Record', to: 'Ingest footage', type: 'finish-to-start' },
  { from: 'Production', to: 'Post Production', type: 'finish-to-start' },
  { from: 'Assembly Cut', to: 'Rough Cut', type: 'finish-to-start' },
  { from: 'Rough Cut', to: 'Final Cut', type: 'finish-to-start' },
  { from: 'Post Production', to: 'Delivery', type: 'finish-to-start' },
];

const videoApprovals: DemoApproval[] = [
  { taskName: 'Concept', approverSlots: ['admin'] },
  { taskName: 'Script', approverSlots: ['admin'] },
  { taskName: 'Storyboard', approverSlots: ['visitor'] },
  { taskName: 'Assembly Cut', approverSlots: ['manager'] },
  { taskName: 'Rough Cut', approverSlots: ['visitor'] },
  { taskName: 'Final Cut', approverSlots: ['admin', 'owner'] },
  { taskName: 'Delivery', approverSlots: ['visitor'] },
];

const videoStoryPoints: DemoStoryPoint[] = VIDEO_STORY;

const videoScenario: DemoScenario = {
  id: 'demo-video',
  name: 'Marketing Video',
  description: 'A production team takes a 60-second video from concept to delivery: phase dependencies, client review with timecoded notes and versions, a live two-person final sign-off and a TaskBot check-in.',
  runtimeMinutes: 12,
  category: 'media',
  scheduleMode: 'daily',
  personas: videoPersonas,
  tasks: videoTasks,
  dependencies: videoDeps,
  approvals: videoApprovals,
  storyPoints: videoStoryPoints,
};

// ═══════════════════════════════════════════
// Export all scenarios
// ═══════════════════════════════════════════

export const DEMO_SCENARIOS: DemoScenario[] = [videoScenario, courseScenario, cakeScenario];

export function getDemoScenarioById(id: string): DemoScenario | undefined {
  return DEMO_SCENARIOS.find(s => s.id === id);
}

/**
 * Detect which demo scenario is active by matching TLP (root) task names
 * against known scenario task lists. Returns the persona map if found.
 */
export function detectDemoPersonaMap(
  taskNames: string[]
): Record<string, { name: string; avatarUrl: string }> | null {
  const nameSet = new Set(taskNames);
  for (const scenario of DEMO_SCENARIOS) {
    // Check if the scenario's root task name exists in the task set
    const rootTask = scenario.tasks.find(t => !t.parentName);
    if (rootTask && nameSet.has(rootTask.name)) {
      const map: Record<string, { name: string; avatarUrl: string }> = {};
      for (const [slotKey, persona] of Object.entries(scenario.personas)) {
        map[slotKey] = { name: persona.name, avatarUrl: persona.avatarUrl };
      }
      return map;
    }
  }
  return null;
}
